import React, { useEffect, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { useAtom } from 'jotai'
import { AxiosResponse } from 'axios'
import { useParams } from 'react-router-dom'
import { CaseDetail } from '../../../../models/CaseDetail'
import {
    EmploymentType,
    EmploymentTypeData,
} from '../../../../models/EmploymentType'
import { formatDate } from '../../../../utils/format'
import {
    Help,
    IkonContainerNyansattesoplysninger,
    IkonPromptfejl,
    IkonRedigeroplysninger,
} from '../../../../assets/icons'
import { FormControl } from '../../../ui/FormElememts/FormControl'
import { FormInputNumber } from '../../../modules/FormElements/FormInput'
import { Select } from '../../../ui/FormElememts/Select'
import Input from '../../../ui/FormElememts/Input'
import { Button } from '../../../ui/Button/Button'
import { CardColumn } from '../../../ui/Card/CardColumn'
import { colors } from '../../../../theme/palette'
import { L } from '../../../providers/Lokalise'
import { Typography } from '../../../ui/Typography/Typography'
import {
    getEmploymentTypeList,
    updateCaseBirthdate,
    updateCaseEmail,
    updateCaseHireTypeAndPeriod,
    updateCaseHoursPerWeek,
    updateCaseMobileNumber,
} from '../../../../api/salary'
import { profileAtom } from '../../../../api/profile/profile'
import { SimpleDialog } from '../../../ui/Dialog/SimpleDialog'
import {
    contactDialogStateAtom,
    ErrorDisplay,
    initialContactDialogState,
} from '../Contact/ContactDialog'
import { Tooltip } from '../../../ui/Tooltip/Tooltip'
import { caseDetailsUnsavedChangesAtom } from './CloseCaseRow'
import { DatePickerInput } from '../../../ui/FormElememts/DatePickerInput'
import { FormMessage } from '../../../ui/FormElememts/FormMessage'

const useStyles = createUseStyles({
    textWithIcon: {
        width: '1rem',
        height: '1rem',
        border: `1px solid ${colors.red600}`,
        borderRadius: '50%',
        marginLeft: '5px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    icon: {
        width: '1rem',
        height: '1rem',
        borderRadius: '50%',
    },
    editButton: {
        color: colors.green600,
        fill: colors.green600,
        width: '2rem',
        height: '2rem',
        border: 'none',
        background: 'none',
        position: 'absolute',
        right: '0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        top: '0',
        bottom: '0',
        margin: 'auto',
        cursor: 'pointer',
        '&:disabled': {
            opacity: 0.7,
            color: colors.gray800,
            fill: colors.gray800,
            cursor: 'not-allowed',
        },
    },
    rowContainer: {
        position: 'relative',
        width: '100%',
        maxWidth: '100%',
        minHeight: '39px',
    },
    buttonContainer: {
        display: 'flex',
        gap: '0.2rem',
        marginTop: '1rem',
        cursor: 'pointer',
        '& button': {
            flexGrow: 1,
            maxWidth: '50%',
        },
    },
})

export type CaseDetailsProps = {
    caseDetails: CaseDetail
    updateData: (data: CaseDetail) => void
}

const formatEmploymentPeriod = (
    startDate: string,
    endDate: string,
    numberOfMonths: number
): string => {
    const start = new Date(startDate)
    const end = new Date(endDate)
    const monthLabel = numberOfMonths === 1 ? 'måned' : 'måneder'

    return `${formatDate(start)} - ${formatDate(
        end
    )} (${numberOfMonths} ${monthLabel})`
}

const employmentTypeHasEndDate = (
    employmentType: string | number | null | undefined
): boolean => [6, 16].includes(Number(employmentType))

const PesonalDetails = ({
    caseDetails,
    updateData,
}: CaseDetailsProps): JSX.Element => {
    const [authProfile] = useAtom(profileAtom)
    const classes = useStyles()
    const params = useParams()
    const [isLoading, setIsLoading] = useState(false)
    const [isMobileNumberEdit, setIsMobileNumberEdit] = useState(false)
    const [isEmailEdit, setIsEmailEdit] = useState(false)
    const [isBirthdateEdit, setIsBirthdayEdit] = useState(false)
    const [isHireDateEdit, setIsHireDateEdit] = useState(false)
    const [isEmploymentTypeEdit, setIsEmploymentTypeEdit] = useState(false)
    const [isHoursPerWeekEdit, setIsHoursPerWeekEdit] = useState(false)
    const [hireDateError, setHireDateError] = useState<{
        hasError: boolean
        message: string
        show: boolean
    }>({
        hasError: false,
        message: 'Datoen er ikke gyldig',
        show: false,
    })
    const [birthdateError, setBirthdateError] = useState<{
        hasError: boolean
        message: string
        show: boolean
    }>({
        hasError: false,
        message: 'Datoen er ikke gyldig',
        show: false,
    })
    const [selectedNumber, setSelectedNumber] = useState<string | null>(
        undefined
    )
    const [selectedEmail, setSelectedEmail] = useState<string | null>(undefined)
    const [selectedBirthdate, setSelectedBirthdate] = useState<string | null>(
        undefined
    )
    const [selectedHireDate, setSelectedHireDate] = useState<string | null>(
        undefined
    )
    const [selectedHireEndDate, setSelectedHireEndDate] = useState<
        string | null
    >(undefined)
    const [selectedEmploymentType, setSelectedEmploymentType] = useState<
        string | null
    >(undefined)
    const [selectedHoursPerWeek, setSelectedHoursPerWeek] = useState<
        string | null
    >(undefined)
    const [employmentTypeList, setEmploymentTypeList] = useState<
        EmploymentType[]
    >([])
    const [hireEndDateError, setHireEndDateError] = useState({
        hasError: false,
        message: 'Datoen er ikke gyldig',
        show: false,
    })
    const [errorMessage, setErrorMessage] = useState<React.ReactNode>('')

    const [openErrorDialog, setOpenErrorDialog] = useState<boolean>(false)
    const [contactDialogState, setContactDialogState] = useAtom(
        contactDialogStateAtom
    )
    const [caseDetailsUnsavedChanges, setCaseDetailsUnsavedChanges] = useAtom(
        caseDetailsUnsavedChangesAtom
    )
    const canProcessCase = Boolean(
        caseDetails?.allowedUpdateActionList?.includes('Sagsbehandle')
    )
    const currentEmploymentTypeId = employmentTypeList.find(
        (item) => item.name === caseDetails?.newlyHired.employmentType
    )?.id
    const isFixedTermCase =
        caseDetails?.newlyHired.employmentType === 'Tidsbegrænset' ||
        caseDetails?.newlyHired.employmentType === 'Tidsbegrænset fleksjob' ||
        currentEmploymentTypeId === 6 ||
        currentEmploymentTypeId === 16

    useEffect(() => {
        const fetchEmploymentTypeList = async (): Promise<void> => {
            const response = await getEmploymentTypeList()
            const list = Array.isArray(response) ? response : []
            const mapped = list.map((item: EmploymentTypeData) =>
                EmploymentType.build(item)
            )
            setEmploymentTypeList(mapped)
        }
        fetchEmploymentTypeList()
    }, [])

    const resetData = (): void => {
        setIsMobileNumberEdit(false)
        setSelectedNumber(null)
        setIsEmailEdit(false)
        setSelectedEmail(null)
        setIsBirthdayEdit(false)
        setSelectedBirthdate(null)
        setIsHireDateEdit(false)
        setSelectedHireDate(null)
        setSelectedHireEndDate(null)
        setIsEmploymentTypeEdit(false)
        setSelectedEmploymentType(null)
        setIsHoursPerWeekEdit(false)
        setSelectedHoursPerWeek(null)
    }
    const submitNumber = async (): Promise<AxiosResponse> => {
        const { id } = params
        const regTid = caseDetails?.caseRegistrationTime
        if (selectedNumber || selectedNumber === '') {
            setIsLoading(true)
            const response = await updateCaseMobileNumber(
                id,
                regTid,
                selectedNumber === '' ? null : selectedNumber ?? null
            )

            if (response.error) {
                setErrorMessage(<ErrorDisplay errors={response.error.errors} />)
                setOpenErrorDialog(true)
                setIsLoading(false)
            } else if (response.status === 200) {
                resetData()
                const details = CaseDetail.build(response.data)
                updateData(details)
            }
            setIsLoading(false)
        }
        return Promise.resolve({} as AxiosResponse)
    }
    const submitEmail = async (): Promise<AxiosResponse> => {
        const { id } = params
        const regTid = caseDetails?.caseRegistrationTime
        if (selectedEmail || selectedEmail === '') {
            setIsLoading(true)
            const response = await updateCaseEmail(
                id,
                regTid,
                selectedEmail === '' ? null : selectedEmail ?? null
            )
            if (response.error) {
                setErrorMessage(<ErrorDisplay errors={response.error.errors} />)
                setOpenErrorDialog(true)
                setIsLoading(false)
            } else if (response.status === 200) {
                resetData()
                const details = CaseDetail.build(response.data)
                updateData(details)
                setIsLoading(false)
            }
        }
        return Promise.resolve({} as AxiosResponse)
    }

    const correctFormat = (date: Date): string | null => {
        if (!date || Number.isNaN(date.getTime())) {
            return null
        }
        return `${date.getFullYear()}-${`0${date.getMonth() + 1}`.slice(
            -2
        )}-${`0${date.getDate()}`.slice(-2)}`
    }
    const submitBirthdate = async (): Promise<AxiosResponse> => {
        if (birthdateError.hasError) {
            setTimeout(() => {
                setBirthdateError((prev) => ({ ...prev, show: true }))
            }, 100)
            return Promise.resolve({} as AxiosResponse)
        }
        const { id } = params
        const regTid = caseDetails?.caseRegistrationTime
        const correctDateFormat = correctFormat(new Date(selectedBirthdate))
        setIsLoading(true)
        const response = await updateCaseBirthdate(
            id,
            regTid,
            selectedBirthdate === correctDateFormat
                ? null
                : correctDateFormat ?? null
        )
        if (response.error) {
            setErrorMessage(<ErrorDisplay errors={response.error.errors} />)
            setOpenErrorDialog(true)
            setIsLoading(false)
        } else if (response.status === 200) {
            // reset contactDialogStateAtom
            setContactDialogState(initialContactDialogState)
            resetData()
            const details = CaseDetail.build(response.data)
            updateData(details)
        }
        setIsLoading(false)
        return Promise.resolve({} as AxiosResponse)
    }
    const submitHireDate = async (): Promise<AxiosResponse> => {
        const employmentTypeId = Number(selectedEmploymentType)
        const hasEmploymentEndDate = employmentTypeHasEndDate(employmentTypeId)
        if (
            hireDateError.hasError ||
            (hasEmploymentEndDate && hireEndDateError.hasError)
        ) {
            setTimeout(() => {
                setHireDateError((prev) => ({ ...prev, show: true }))
                if (hasEmploymentEndDate) {
                    setHireEndDateError((prev) => ({ ...prev, show: true }))
                }
            }, 100)
            return Promise.resolve({} as AxiosResponse)
        }
        const { id } = params
        const regTid = caseDetails?.caseRegistrationTime
        const hireStartDate = selectedHireDate
            ? correctFormat(new Date(selectedHireDate))
            : null
        const hireEndDate =
            hasEmploymentEndDate && selectedHireEndDate
                ? correctFormat(new Date(selectedHireEndDate))
                : null

        setIsLoading(true)

        const response = await updateCaseHireTypeAndPeriod(
            id,
            regTid,
            employmentTypeId,
            hireStartDate,
            hireEndDate
        )
        if (response.error) {
            setErrorMessage(<ErrorDisplay errors={response.error.errors} />)
            setOpenErrorDialog(true)
            setIsLoading(false)
        } else if (response.status === 200) {
            setContactDialogState(initialContactDialogState)
            resetData()
            const details = CaseDetail.build(response.data)
            updateData(details)
        }
        setIsLoading(false)
        return Promise.resolve({} as AxiosResponse)
    }

    const submitHoursPerWeek = async (): Promise<AxiosResponse> => {
        const { id } = params
        const regTid = caseDetails?.caseRegistrationTime
        setIsLoading(true)
        const response = await updateCaseHoursPerWeek(
            id,
            regTid,
            selectedHoursPerWeek === ''
                ? null
                : Number(selectedHoursPerWeek?.replace(',', '.')) ?? null
        )
        if (response.error) {
            setErrorMessage(<ErrorDisplay errors={response.error.errors} />)
            setOpenErrorDialog(true)
            setIsLoading(false)
        } else if (response.status === 200) {
            resetData()
            const details = CaseDetail.build(response.data)
            updateData(details)
        }
        setIsLoading(false)
        return Promise.resolve(response)
    }

    const renderEditMobileEditButton = (): JSX.Element => {
        return (
            <button
                type="button"
                disabled={!canProcessCase || authProfile.isImitator}
                className={classes.editButton}
                onClick={() => {
                    setIsMobileNumberEdit(true)
                    setIsBirthdayEdit(false)
                    setIsHireDateEdit(false)
                    setIsEmploymentTypeEdit(false)
                    setIsHoursPerWeekEdit(false)
                    setIsEmailEdit(false)
                    if (
                        caseDetails?.newlyHired.contactInfo.mobileNumbers
                            .length === 1
                    ) {
                        setSelectedNumber(
                            caseDetails?.newlyHired.contactInfo.mobileNumbers[0]
                        )
                    }
                }}
            >
                {!canProcessCase ? (
                    <Tooltip
                        content={L(
                            'LØN_TOOLTIP_TAG_SAGEN_FOR_AT_BRUGE_FUNKTION'
                        )}
                    >
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                            <IkonRedigeroplysninger
                                style={{
                                    width: '1.5rem',
                                    height: '1.5rem',
                                }}
                            />
                        </span>
                    </Tooltip>
                ) : (
                    <IkonRedigeroplysninger
                        style={{
                            width: '1.5rem',
                            height: '1.5rem',
                        }}
                    />
                )}
            </button>
        )
    }

    const renderEditEmailButton = (): JSX.Element => {
        return (
            <button
                type="button"
                disabled={!canProcessCase || authProfile.isImitator}
                className={classes.editButton}
                onClick={() => {
                    setIsEmailEdit(true)
                    setIsBirthdayEdit(false)
                    setIsHireDateEdit(false)
                    setIsEmploymentTypeEdit(false)
                    setIsHoursPerWeekEdit(false)
                    setIsMobileNumberEdit(false)
                    if (
                        caseDetails?.newlyHired.contactInfo.emails.length === 1
                    ) {
                        setSelectedEmail(
                            caseDetails?.newlyHired.contactInfo.emails[0]
                        )
                    }
                }}
            >
                {!canProcessCase ? (
                    <Tooltip
                        content={L(
                            'LØN_TOOLTIP_TAG_SAGEN_FOR_AT_BRUGE_FUNKTION'
                        )}
                    >
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                            <IkonRedigeroplysninger
                                style={{
                                    width: '1.5rem',
                                    height: '1.5rem',
                                }}
                            />
                        </span>
                    </Tooltip>
                ) : (
                    <IkonRedigeroplysninger
                        style={{
                            width: '1.5rem',
                            height: '1.5rem',
                        }}
                    />
                )}
            </button>
        )
    }

    const renderBirthdateEditButton = (): JSX.Element => {
        return (
            <button
                type="button"
                disabled={!canProcessCase || authProfile.isImitator}
                className={classes.editButton}
                onClick={() => {
                    setIsBirthdayEdit(true)
                    setIsHireDateEdit(false)
                    setIsEmploymentTypeEdit(false)
                    setIsHoursPerWeekEdit(false)
                    setIsEmailEdit(false)
                    setIsMobileNumberEdit(false)
                    setSelectedBirthdate(caseDetails?.newlyHired.dateOfBirth)
                }}
            >
                {!canProcessCase ? (
                    <Tooltip
                        content={L(
                            'LØN_TOOLTIP_TAG_SAGEN_FOR_AT_BRUGE_FUNKTION'
                        )}
                    >
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                            <IkonRedigeroplysninger
                                style={{
                                    width: '1.5rem',
                                    height: '1.5rem',
                                }}
                            />
                        </span>
                    </Tooltip>
                ) : (
                    <IkonRedigeroplysninger
                        style={{
                            width: '1.5rem',
                            height: '1.5rem',
                        }}
                    />
                )}
            </button>
        )
    }

    const renderHireDateEditButton = (): JSX.Element => {
        return (
            <button
                type="button"
                disabled={!canProcessCase || authProfile.isImitator}
                className={classes.editButton}
                onClick={() => {
                    setIsHireDateEdit(true)
                    setIsEmploymentTypeEdit(true)
                    setIsBirthdayEdit(false)
                    setIsEmailEdit(false)
                    setIsMobileNumberEdit(false)
                    setIsHoursPerWeekEdit(false)
                    setSelectedHireDate(
                        caseDetails?.newlyHired.startDateOfEmployment ||
                            caseDetails?.newlyHired.dateOfEmployment
                    )
                    setSelectedHireEndDate(
                        caseDetails?.newlyHired.endDateOfEmployment
                    )
                    const currentType = employmentTypeList.find(
                        (item) =>
                            item.name ===
                            caseDetails?.newlyHired?.employmentType
                    )
                    setSelectedEmploymentType(
                        currentType ? String(currentType.id) : null
                    )
                }}
            >
                {!canProcessCase ? (
                    <Tooltip
                        content={L(
                            'LØN_TOOLTIP_TAG_SAGEN_FOR_AT_BRUGE_FUNKTION'
                        )}
                    >
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                            <IkonRedigeroplysninger
                                style={{
                                    width: '1.5rem',
                                    height: '1.5rem',
                                }}
                            />
                        </span>
                    </Tooltip>
                ) : (
                    <IkonRedigeroplysninger
                        style={{
                            width: '1.5rem',
                            height: '1.5rem',
                        }}
                    />
                )}
            </button>
        )
    }

    const renderEmploymentTypeEditButton = (): JSX.Element => {
        return (
            <button
                type="button"
                disabled={!canProcessCase || authProfile.isImitator}
                className={classes.editButton}
                onClick={() => {
                    setIsEmploymentTypeEdit(true)
                    setIsHireDateEdit(true)
                    setIsBirthdayEdit(false)
                    setIsEmailEdit(false)
                    setIsMobileNumberEdit(false)
                    setIsHoursPerWeekEdit(false)
                    setSelectedHireDate(
                        caseDetails?.newlyHired.startDateOfEmployment ||
                            caseDetails?.newlyHired.dateOfEmployment
                    )
                    setSelectedHireEndDate(
                        caseDetails?.newlyHired.endDateOfEmployment
                    )
                    const currentType = employmentTypeList.find(
                        (item) =>
                            item.name ===
                            caseDetails?.newlyHired?.employmentType
                    )
                    setSelectedEmploymentType(
                        currentType ? String(currentType.id) : null
                    )
                }}
            >
                {!canProcessCase ? (
                    <Tooltip
                        content={L(
                            'LØN_TOOLTIP_TAG_SAGEN_FOR_AT_BRUGE_FUNKTION'
                        )}
                    >
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                            <IkonRedigeroplysninger
                                style={{
                                    width: '1.5rem',
                                    height: '1.5rem',
                                }}
                            />
                        </span>
                    </Tooltip>
                ) : (
                    <IkonRedigeroplysninger
                        style={{
                            width: '1.5rem',
                            height: '1.5rem',
                        }}
                    />
                )}
            </button>
        )
    }

    const renderHoursPerWeekEditButton = (): JSX.Element => {
        return (
            <button
                type="button"
                disabled={!canProcessCase || authProfile.isImitator}
                className={classes.editButton}
                onClick={() => {
                    setIsHoursPerWeekEdit(true)
                    setIsHireDateEdit(false)
                    setIsEmploymentTypeEdit(false)
                    setIsBirthdayEdit(false)
                    setIsEmailEdit(false)
                    setIsMobileNumberEdit(false)
                    setSelectedHoursPerWeek(
                        caseDetails?.newlyHired?.hoursPerWeek != null
                            ? String(
                                  caseDetails.newlyHired.hoursPerWeek
                              ).replace('.', ',')
                            : null
                    )
                }}
            >
                {!canProcessCase ? (
                    <Tooltip
                        content={L(
                            'LØN_TOOLTIP_TAG_SAGEN_FOR_AT_BRUGE_FUNKTION'
                        )}
                    >
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                            <IkonRedigeroplysninger
                                style={{
                                    width: '1.5rem',
                                    height: '1.5rem',
                                }}
                            />
                        </span>
                    </Tooltip>
                ) : (
                    <IkonRedigeroplysninger
                        style={{
                            width: '1.5rem',
                            height: '1.5rem',
                        }}
                    />
                )}
            </button>
        )
    }

    return (
        <>
            <CardColumn
                titleLabel={L('LØN_NYANSATTES_OPLYSNINGER__CONTAINER_TITEL')}
                titleIcon={
                    <IkonContainerNyansattesoplysninger
                        style={{ color: colors.green600 }}
                        fill={colors.green}
                    />
                }
                rows={[
                    {
                        label: '', // Medlemsskab
                        value: '',
                        useContent: true,
                        content: (
                            <div>
                                <Typography
                                    tag="h4"
                                    size="XXS"
                                    color=" rgba(0, 0, 0, 0.4)"
                                >
                                    {L(
                                        'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_MEDLEMSSKAB_LABEL'
                                    )}
                                </Typography>
                                {caseDetails?.newlyHired
                                    ?.foaMemberDisplayText === 'Ikke fundet' ? (
                                    <span
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: colors.red600,
                                        }}
                                    >
                                        <span>
                                            {L(
                                                'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_MEDLEM_IKKE_FUNDET_LABEL'
                                            )}
                                        </span>
                                        <Tooltip
                                            content={L(
                                                'LØN_DETALJE_MEDLEM_IKKE_FUNDET_TOOLTIP'
                                            )}
                                        >
                                            <span
                                                className={classes.textWithIcon}
                                            >
                                                <Help
                                                    className={classes.icon}
                                                />
                                            </span>
                                        </Tooltip>
                                    </span>
                                ) : (
                                    <Typography tag="p" size="XS">
                                        {L(
                                            'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_MEDLEM_FUNDET_LABEL'
                                        )}
                                    </Typography>
                                )}
                            </div>
                        ),
                    },
                    {
                        label: '', // Mobil
                        value: '',
                        useContent: true,
                        content: (
                            <div className={classes.rowContainer}>
                                {isMobileNumberEdit ? (
                                    <FormControl>
                                        {caseDetails?.newlyHired.contactInfo
                                            .mobileNumbers.length > 1 &&
                                        typeof selectedNumber !== 'string' ? (
                                            <>
                                                <Select
                                                    // placeholder="Mobil"
                                                    placeholder={L(
                                                        'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_MOBIL_LABEL'
                                                    )}
                                                    onChange={(e) => {
                                                        setSelectedNumber(
                                                            e.target.value
                                                        )
                                                    }}
                                                    options={[
                                                        ...caseDetails?.newlyHired.contactInfo.mobileNumbers.map(
                                                            (number) => ({
                                                                label: number,
                                                                value: String(
                                                                    number
                                                                ),
                                                            })
                                                        ),
                                                        {
                                                            label: 'Andet',
                                                            value: '',
                                                        },
                                                    ]}
                                                />
                                                <div
                                                    className={
                                                        classes.buttonContainer
                                                    }
                                                >
                                                    <Button
                                                        buttonType="button"
                                                        type="secondary"
                                                        onClick={() => {
                                                            setIsMobileNumberEdit(
                                                                false
                                                            )
                                                            setSelectedNumber(
                                                                undefined
                                                            )
                                                            setCaseDetailsUnsavedChanges(
                                                                false
                                                            )
                                                        }}
                                                    >
                                                        {L(
                                                            'LØN_DETALJE_ANNULLER_KNAP'
                                                        )}
                                                    </Button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <FormInputNumber
                                                    name="mobil"
                                                    required={false}
                                                    // label="Mobil"
                                                    label={L(
                                                        'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_MOBIL_LABEL'
                                                    )}
                                                    value={selectedNumber}
                                                    onChange={(e) => {
                                                        setCaseDetailsUnsavedChanges(
                                                            true
                                                        )
                                                        setSelectedNumber(
                                                            e.target.value
                                                        )
                                                    }}
                                                />
                                                <div
                                                    className={
                                                        classes.buttonContainer
                                                    }
                                                >
                                                    <Button
                                                        type="secondary"
                                                        onClick={() => {
                                                            setIsMobileNumberEdit(
                                                                false
                                                            )
                                                            setSelectedNumber(
                                                                undefined
                                                            )
                                                            setCaseDetailsUnsavedChanges(
                                                                false
                                                            )
                                                        }}
                                                    >
                                                        {L(
                                                            'LØN_DETALJE_ANNULLER_KNAP'
                                                        )}
                                                    </Button>
                                                    <Button
                                                        type="primary"
                                                        loading={isLoading}
                                                        onClick={submitNumber}
                                                    >
                                                        {L(
                                                            'LØN_DETALJE_GEM_KNAP'
                                                        )}
                                                    </Button>
                                                </div>
                                            </>
                                        )}
                                    </FormControl>
                                ) : (
                                    <>
                                        <Typography
                                            tag="h4"
                                            size="XXS"
                                            color=" rgba(0, 0, 0, 0.4)"
                                        >
                                            {/* Mobil */}
                                            {L(
                                                'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_MOBIL_LABEL'
                                            )}
                                        </Typography>
                                        <Typography
                                            tag="p"
                                            size="XS"
                                            color={
                                                caseDetails?.newlyHired
                                                    ?.contactInfo?.mobileNumbers
                                                    ?.length > 1
                                                    ? colors.caseRed500
                                                    : colors.black
                                            }
                                        >
                                            {caseDetails?.newlyHired.contactInfo
                                                .mobileNumbers.length > 1
                                                ? L(
                                                      'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_FLERE_FUNDET_LABEL'
                                                  )
                                                : caseDetails?.newlyHired?.contactInfo?.mobileNumbers?.toString() ??
                                                  ''}
                                        </Typography>
                                        {caseDetails &&
                                            !caseDetails?.isClosed &&
                                            renderEditMobileEditButton()}
                                    </>
                                )}
                            </div>
                        ),
                    },
                    {
                        label: '', // Email
                        value: '',
                        useContent: true,
                        content: (
                            <div className={classes.rowContainer}>
                                {isEmailEdit ? (
                                    <FormControl>
                                        {caseDetails?.newlyHired.contactInfo
                                            .emails.length > 1 &&
                                        typeof selectedEmail !== 'string' ? (
                                            <>
                                                <Select
                                                    // placeholder="Email"
                                                    placeholder={L(
                                                        'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_EMAIL_LABEL'
                                                    )}
                                                    onChange={(e) => {
                                                        setSelectedEmail(
                                                            e.target.value
                                                        )
                                                    }}
                                                    options={[
                                                        ...caseDetails?.newlyHired.contactInfo.emails.map(
                                                            (address) => ({
                                                                label: address,
                                                                value: address,
                                                            })
                                                        ),
                                                        {
                                                            label: 'Andet',
                                                            value: '',
                                                        },
                                                    ]}
                                                />
                                                <div
                                                    className={
                                                        classes.buttonContainer
                                                    }
                                                >
                                                    <Button
                                                        buttonType="button"
                                                        type="secondary"
                                                        onClick={() => {
                                                            setIsEmailEdit(
                                                                false
                                                            )
                                                            setSelectedEmail(
                                                                undefined
                                                            )
                                                            setCaseDetailsUnsavedChanges(
                                                                false
                                                            )
                                                        }}
                                                    >
                                                        {L(
                                                            'LØN_DETALJE_ANNULLER_KNAP'
                                                        )}
                                                    </Button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <Input
                                                    required={false}
                                                    // label="Mobil"
                                                    label={L(
                                                        'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_EMAIL_LABEL'
                                                    )}
                                                    value={selectedEmail}
                                                    onChange={(e) => {
                                                        setCaseDetailsUnsavedChanges(
                                                            true
                                                        )
                                                        setSelectedEmail(
                                                            e.target.value
                                                        )
                                                    }}
                                                />
                                                <div
                                                    className={
                                                        classes.buttonContainer
                                                    }
                                                >
                                                    <Button
                                                        type="secondary"
                                                        onClick={() => {
                                                            setIsEmailEdit(
                                                                false
                                                            )
                                                            setSelectedEmail(
                                                                undefined
                                                            )
                                                            setCaseDetailsUnsavedChanges(
                                                                false
                                                            )
                                                        }}
                                                    >
                                                        {L(
                                                            'LØN_DETALJE_ANNULLER_KNAP'
                                                        )}
                                                    </Button>
                                                    <Button
                                                        type="primary"
                                                        loading={isLoading}
                                                        onClick={submitEmail}
                                                    >
                                                        {L(
                                                            'LØN_DETALJE_GEM_KNAP'
                                                        )}
                                                    </Button>
                                                </div>
                                            </>
                                        )}
                                    </FormControl>
                                ) : (
                                    <>
                                        <Typography
                                            tag="h4"
                                            size="XXS"
                                            color=" rgba(0, 0, 0, 0.4)"
                                        >
                                            {/* Email */}
                                            {L(
                                                'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_EMAIL_LABEL'
                                            )}
                                        </Typography>
                                        <Typography
                                            tag="p"
                                            size="XS"
                                            color={
                                                caseDetails?.newlyHired
                                                    ?.contactInfo?.emails
                                                    ?.length > 1
                                                    ? colors.caseRed500
                                                    : colors.black
                                            }
                                        >
                                            {caseDetails?.newlyHired.contactInfo
                                                .emails.length > 1
                                                ? L(
                                                      'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_FLERE_FUNDET_LABEL'
                                                  )
                                                : caseDetails?.newlyHired.contactInfo.emails.toString()}
                                        </Typography>
                                        {caseDetails &&
                                            !caseDetails?.isClosed &&
                                            renderEditEmailButton()}
                                    </>
                                )}
                            </div>
                        ),
                    },
                    {
                        label: '', // Fødselsdato
                        value: '',
                        useContent: true,
                        content: (
                            <div className={classes.rowContainer}>
                                {isBirthdateEdit ? (
                                    <FormControl>
                                        <DatePickerInput
                                            label={L(
                                                'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_FØDSELSDATO_LABEL'
                                            )}
                                            onChangeDateInputInvalid={(
                                                isValid,
                                                message
                                            ) => {
                                                setBirthdateError({
                                                    hasError: !isValid,
                                                    message:
                                                        message ??
                                                        'Datoen er ikke gyldig',
                                                    show: isValid
                                                        ? false
                                                        : birthdateError.show,
                                                })
                                            }}
                                            selected={
                                                selectedBirthdate !== null &&
                                                !!selectedBirthdate
                                                    ? new Date(
                                                          selectedBirthdate
                                                      )
                                                    : null
                                            }
                                            setSelected={(date) => {
                                                const d =
                                                    date === null
                                                        ? undefined
                                                        : date.toDateString()
                                                setCaseDetailsUnsavedChanges(
                                                    true
                                                )
                                                setSelectedBirthdate(d)
                                            }}
                                            minDate={new Date('1900-01-01')}
                                            maxDate={new Date()}
                                            hasError={
                                                birthdateError.hasError &&
                                                birthdateError.show
                                            }
                                        />
                                        {birthdateError.show && (
                                            <FormMessage
                                                text={birthdateError.message}
                                                error={birthdateError.message}
                                            />
                                        )}
                                        <div
                                            className={classes.buttonContainer}
                                        >
                                            <Button
                                                type="secondary"
                                                onClick={() => {
                                                    setIsBirthdayEdit(false)
                                                    setSelectedBirthdate(
                                                        undefined
                                                    )
                                                    setCaseDetailsUnsavedChanges(
                                                        false
                                                    )
                                                    setBirthdateError({
                                                        hasError: false,
                                                        message:
                                                            'Datoen er ikke gyldig',
                                                        show: false,
                                                    })
                                                }}
                                            >
                                                {L('LØN_DETALJE_ANNULLER_KNAP')}
                                            </Button>
                                            <Button
                                                type="primary"
                                                loading={isLoading}
                                                onClick={submitBirthdate}
                                            >
                                                {L('LØN_DETALJE_GEM_KNAP')}
                                            </Button>
                                        </div>
                                    </FormControl>
                                ) : (
                                    <>
                                        <Typography
                                            tag="h4"
                                            size="XXS"
                                            color=" rgba(0, 0, 0, 0.4)"
                                        >
                                            {/* Fødselsdato */}
                                            {L(
                                                'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_FØDSELSDATO_LABEL'
                                            )}
                                        </Typography>
                                        <Typography tag="p" size="XS">
                                            {caseDetails?.newlyHired
                                                ?.dateOfBirth &&
                                            caseDetails?.newlyHired
                                                ?.dateOfBirth !== null
                                                ? `${formatDate(
                                                      new Date(
                                                          caseDetails?.newlyHired.dateOfBirth
                                                      )
                                                  )} (${
                                                      caseDetails?.newlyHired
                                                          .age
                                                  } år)`
                                                : ''}
                                        </Typography>
                                        {caseDetails &&
                                            !caseDetails?.isClosed &&
                                            renderBirthdateEditButton()}
                                    </>
                                )}
                            </div>
                        ),
                    },
                    {
                        label: L(
                            'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_ARBEJDSSTED_LABEL'
                        ),
                        value: caseDetails?.newlyHired?.workplace?.navn,
                    },
                    {
                        label: L(
                            'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_STILLING_LABEL'
                        ),
                        value: caseDetails?.newlyHired?.position?.navn,
                    },
                    ...(isHireDateEdit || isEmploymentTypeEdit
                        ? [
                              {
                                  label: '',
                                  value: '',
                                  useContent: true,
                                  content: (
                                      <div className={classes.rowContainer}>
                                          <FormControl>
                                              <Select
                                                  useControlled
                                                  options={employmentTypeList.map(
                                                      (item) => ({
                                                          label: item.name,
                                                          value: String(
                                                              item.id
                                                          ),
                                                      })
                                                  )}
                                                  placeholder={L(
                                                      'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_ANSÆTTELSES_TYPE_LABEL'
                                                  )}
                                                  value={
                                                      selectedEmploymentType ??
                                                      undefined
                                                  }
                                                  onChange={(e) => {
                                                      setCaseDetailsUnsavedChanges(
                                                          true
                                                      )
                                                      setSelectedEmploymentType(
                                                          e.target.value
                                                      )
                                                  }}
                                              />
                                          </FormControl>
                                          <FormControl>
                                              <DatePickerInput
                                                  label={L(
                                                      'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_ANSÆTTELSES_SLUTDATO_LABEL'
                                                  )}
                                                  selected={
                                                      selectedHireDate !==
                                                          null &&
                                                      !!selectedHireDate
                                                          ? new Date(
                                                                selectedHireDate
                                                            )
                                                          : null
                                                  }
                                                  onChangeDateInputInvalid={(
                                                      isValid,
                                                      message
                                                  ) => {
                                                      setHireDateError({
                                                          hasError: !isValid,
                                                          message:
                                                              message ??
                                                              'Datoen er ikke gyldig',
                                                          show: isValid
                                                              ? false
                                                              : hireDateError.show,
                                                      })
                                                  }}
                                                  setSelected={(date) => {
                                                      const d =
                                                          date === null
                                                              ? undefined
                                                              : date.toDateString()
                                                      setCaseDetailsUnsavedChanges(
                                                          true
                                                      )
                                                      setSelectedHireDate(d)
                                                  }}
                                                  minDate={
                                                      new Date('1900-01-01')
                                                  }
                                              />
                                              {hireDateError.show && (
                                                  <FormMessage
                                                      text={
                                                          hireDateError.message
                                                      }
                                                      error={
                                                          hireDateError.message
                                                      }
                                                  />
                                              )}
                                          </FormControl>
                                          {employmentTypeHasEndDate(
                                              selectedEmploymentType
                                          ) && (
                                              <FormControl>
                                                  <DatePickerInput
                                                      label={L(
                                                          'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_ANSÆTTELSES_SLUTDATO_LABEL'
                                                      )}
                                                      selected={
                                                          selectedHireEndDate
                                                              ? new Date(
                                                                    selectedHireEndDate
                                                                )
                                                              : null
                                                      }
                                                      onChangeDateInputInvalid={(
                                                          isValid,
                                                          message
                                                      ) => {
                                                          setHireEndDateError({
                                                              hasError:
                                                                  !isValid,
                                                              message:
                                                                  message ??
                                                                  'Datoen er ikke gyldig',
                                                              show: isValid
                                                                  ? false
                                                                  : hireEndDateError.show,
                                                          })
                                                      }}
                                                      setSelected={(date) => {
                                                          setCaseDetailsUnsavedChanges(
                                                              true
                                                          )
                                                          setSelectedHireEndDate(
                                                              date === null
                                                                  ? undefined
                                                                  : date.toDateString()
                                                          )
                                                      }}
                                                      minDate={
                                                          selectedHireDate
                                                              ? new Date(
                                                                    selectedHireDate
                                                                )
                                                              : new Date(
                                                                    '1900-01-01'
                                                                )
                                                      }
                                                  />
                                                  {hireEndDateError.show && (
                                                      <FormMessage
                                                          text={
                                                              hireEndDateError.message
                                                          }
                                                          error={
                                                              hireEndDateError.message
                                                          }
                                                      />
                                                  )}
                                              </FormControl>
                                          )}
                                          <div
                                              className={
                                                  classes.buttonContainer
                                              }
                                          >
                                              <Button
                                                  type="secondary"
                                                  onClick={() => {
                                                      setCaseDetailsUnsavedChanges(
                                                          false
                                                      )
                                                      setIsHireDateEdit(false)
                                                      setIsEmploymentTypeEdit(
                                                          false
                                                      )
                                                      setSelectedHireDate(
                                                          undefined
                                                      )
                                                      setSelectedHireEndDate(
                                                          undefined
                                                      )
                                                      setSelectedEmploymentType(
                                                          null
                                                      )
                                                      setHireDateError({
                                                          hasError: false,
                                                          message:
                                                              'Datoen er ikke gyldig',
                                                          show: false,
                                                      })
                                                      setHireEndDateError({
                                                          hasError: false,
                                                          message:
                                                              'Datoen er ikke gyldig',
                                                          show: false,
                                                      })
                                                  }}
                                              >
                                                  {L(
                                                      'LØN_DETALJE_ANNULLER_KNAP'
                                                  )}
                                              </Button>
                                              <Button
                                                  type="primary"
                                                  loading={isLoading}
                                                  onClick={submitHireDate}
                                              >
                                                  {L('LØN_DETALJE_GEM_KNAP')}
                                              </Button>
                                          </div>
                                      </div>
                                  ),
                              },
                          ]
                        : [
                              {
                                  label: '',
                                  value: '',
                                  useContent: true,
                                  content: (
                                      <div className={classes.rowContainer}>
                                          <Typography
                                              tag="h4"
                                              size="XXS"
                                              color=" rgba(0, 0, 0, 0.4)"
                                          >
                                              {L(
                                                  'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_ANSÆTTELSES_TYPE_LABEL'
                                              )}
                                          </Typography>
                                          <Typography tag="p" size="XS">
                                              {caseDetails?.newlyHired
                                                  ?.employmentType ?? ''}
                                          </Typography>
                                          {caseDetails &&
                                              !caseDetails?.isClosed &&
                                              renderEmploymentTypeEditButton()}
                                      </div>
                                  ),
                              },
                              {
                                  label: '',
                                  value: '',
                                  useContent: true,
                                  content: (
                                      <div className={classes.rowContainer}>
                                          <Typography
                                              tag="h4"
                                              size="XXS"
                                              color=" rgba(0, 0, 0, 0.4)"
                                          >
                                              {L(
                                                  'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_ANSÆTTELSES_DATO_LABEL'
                                              )}
                                          </Typography>
                                          <Typography tag="p" size="XS">
                                              {isFixedTermCase &&
                                              caseDetails?.newlyHired
                                                  .startDateOfEmployment &&
                                              caseDetails?.newlyHired
                                                  .endDateOfEmployment
                                                  ? formatEmploymentPeriod(
                                                        caseDetails.newlyHired
                                                            .startDateOfEmployment,
                                                        caseDetails.newlyHired
                                                            .endDateOfEmployment,
                                                        caseDetails.newlyHired
                                                            .numberOfMonthsOfEmployment
                                                    )
                                                  : caseDetails?.newlyHired
                                                        .dateOfEmployment
                                                  ? formatDate(
                                                        new Date(
                                                            caseDetails.newlyHired.dateOfEmployment
                                                        )
                                                    )
                                                  : ''}
                                          </Typography>
                                          {caseDetails &&
                                              !caseDetails?.isClosed &&
                                              renderHireDateEditButton()}
                                      </div>
                                  ),
                              },
                          ]),
                    {
                        label: '',
                        value: '',
                        useContent: true,
                        content: (
                            <div className={classes.rowContainer}>
                                {isHoursPerWeekEdit ? (
                                    <FormControl>
                                        <FormInputNumber
                                            name="hoursPerWeek"
                                            required={false}
                                            useFloats
                                            label="Timetal"
                                            value={selectedHoursPerWeek}
                                            onChange={(e) => {
                                                setCaseDetailsUnsavedChanges(
                                                    true
                                                )
                                                setSelectedHoursPerWeek(
                                                    e.target.value
                                                )
                                            }}
                                        />
                                        <div
                                            className={classes.buttonContainer}
                                        >
                                            <Button
                                                type="secondary"
                                                onClick={() => {
                                                    setCaseDetailsUnsavedChanges(
                                                        false
                                                    )
                                                    setIsHoursPerWeekEdit(false)
                                                    setSelectedHoursPerWeek(
                                                        null
                                                    )
                                                }}
                                            >
                                                {L('LØN_DETALJE_ANNULLER_KNAP')}
                                            </Button>
                                            <Button
                                                type="primary"
                                                loading={isLoading}
                                                onClick={submitHoursPerWeek}
                                            >
                                                {L('LØN_DETALJE_GEM_KNAP')}
                                            </Button>
                                        </div>
                                    </FormControl>
                                ) : (
                                    <>
                                        <Typography
                                            tag="h4"
                                            size="XXS"
                                            color=" rgba(0, 0, 0, 0.4)"
                                        >
                                            Timetal
                                        </Typography>
                                        <Typography tag="p" size="XS">
                                            {caseDetails?.newlyHired
                                                ?.hoursPerWeek != null
                                                ? Number(
                                                      caseDetails.newlyHired
                                                          .hoursPerWeek
                                                  )
                                                      .toFixed(2)
                                                      .replace('.', ',')
                                                : ''}
                                        </Typography>
                                        {caseDetails &&
                                            !caseDetails?.isClosed &&
                                            renderHoursPerWeekEditButton()}
                                    </>
                                )}
                            </div>
                        ),
                    },
                ]}
            />
            {openErrorDialog && (
                <SimpleDialog
                    title="Fejl"
                    message={errorMessage}
                    closeDialog={() => setOpenErrorDialog(false)}
                    isError
                    icon={<IkonPromptfejl width="46px" fill={colors.red500} />}
                />
            )}
        </>
    )
}

export default PesonalDetails
